/**
 * Read-time query (W1-06): how long before its hit a note is on screen (checker read-time rules, lane tooltips).
 *
 * The note is sampled backwards from its hit time on Phira's 60 Hz frame grid (time - k / 60, never before chart
 * time 0 nor before its visibleTime step) with every rule Phira applies in autoplay: UI-mover line, line alpha < 0,
 * aggressive culling (its group walked from the head), cover rules, the +-1 off-screen quad cull, and a drawn alpha
 * byte > 0. The first visible frame going backwards starts the run that ends at the hit; the run extends back
 * while the note stays visible. Result: hit time - earliest frame of that run, or null when the note is never
 * visible before its hit. Positions use the engine's line states; the aspect ratio of note placement comes from
 * the given window size (Phira's letterboxing).
 *
 * Look-back window: both phases probe at most APPEAR_LOOKBACK_FRAMES frames (APPEAR_LOOKBACK_SEC seconds) before
 * the hit, so one query costs at most that many `ctx.lineState` calls whatever the chart length. A note that is
 * not drawn at any frame of the window (a hidden note: plain negative-speed notes under aggressive culling, a line
 * whose alpha stays below 0, a note parked off screen) reports null after a bounded scan instead of probing back
 * to chart time 0; a note on screen during the whole window reports APPEAR_LOOKBACK_SEC ("a minute or more").
 * Read-time rules compare against fractions of a second, so the window never changes their verdicts (change
 * request CR-W1-06-8).
 *
 * Bounded query (`atLeast`, change request CR-W2-12b-3): with N = ceil(atLeast * 60 - 1e-9), phase 1 is unchanged
 * (it still tells "never visible" from "first visible later"); a first visible frame beyond N is returned at once,
 * and phase 2 extends the run only up to frame N + 1. Every answer to "read time < atLeast?" is unchanged (a run of
 * at most N frames is measured exactly; a longer one reports at least (N + 1) / 60), and a note visible right before
 * its hit costs at most N + 1 probes instead of up to 3600.
 *
 * Cost per probed frame: one `ctx.lineState` plus O(1) note work (the cull test uses the prefix-max index of
 * culling.ts instead of walking the group from its head, change request CR-W1-06-9). Notes that can never be drawn
 * (alpha 0, a UI-mover line) return null without probing.
 */

import type { NoteId, PackageSettings } from '../../model/types';
import type { EngineCompileContext, PhiraPlayerSettings, SkinMetrics } from '../types';
import { FRAME_DT } from './constants';
import { culledInWalk, cullLimits, type CullLimits } from './culling';
import { frameEnv, makeLineEnv, NoteEval, setLineEnv, type LineEnv } from './env';
import { phiraViewport } from './geometry';
import { refreshMultiFlags } from './multiHint';
import { evalNote } from './noteGeom';
import type { LineNotesRt } from './runtime';
import { locateNote, syncAll } from './store';
import { isGone } from './visibility';

/** Seconds before the hit the query looks at (see the file header). */
export const APPEAR_LOOKBACK_SEC = 60;

/** Frames of the look-back window on the 60 Hz grid (3600). */
export const APPEAR_LOOKBACK_FRAMES = Math.round(APPEAR_LOOKBACK_SEC / FRAME_DT);

interface Probe {
  readonly ctx: EngineCompileContext;
  readonly rt: LineNotesRt;
  readonly i: number;
  readonly ar: number;
  readonly player: PhiraPlayerSettings;
  readonly skin: SkinMetrics;
  readonly pkg: PackageSettings;
  readonly le: LineEnv;
  readonly ev: NoteEval;
  readonly limits: CullLimits;
}

/** Whether Phira's autoplay draws note `p.i` with a visible alpha at chart time t. */
function visibleAt(p: Probe, t: number): boolean {
  const { ctx, rt, i } = p;
  const s = ctx.lineState(rt.line, t);
  if (s.uiMover || s.alpha < 0) return false;
  if (isGone(rt, i, t, null)) return false;
  if (p.player.aggressiveCull) {
    cullLimits(s.m, p.ar, p.limits);
    if (culledInWalk(rt, i, s.height, p.limits, t, null)) return false;
  }
  setLineEnv(p.le, rt, s.m, s.height, s.inclineSin, 1);
  const fe = frameEnv(t, p.ar, 1, p.player, p.skin, null, p.pkg);
  const ev = evalNote(ctx, fe, p.le, i, false, p.ev);
  if (ev.covered || ev.alphaByte === 0) return false;
  for (let q = 0; q < ev.quads; q++) if (ev.offscreen[q] === 0) return true;
  return false;
}

/** EngineNotesModule.noteAppearSec. */
export function noteAppearSec(
  ctx: EngineCompileContext,
  note: NoteId,
  player: PhiraPlayerSettings,
  skin: SkinMetrics,
  winW: number,
  winH: number,
  atLeast?: number,
): number | null {
  const g = syncAll(ctx);
  const loc = locateNote(ctx, note);
  if (loc === null) return null;
  const { rt, i } = loc;
  refreshMultiFlags(rt, g.mh);
  const vp = phiraViewport(winW, winH, ctx.pkg, player);
  const probe: Probe = {
    ctx,
    rt,
    i,
    ar: vp.ar,
    player,
    skin,
    pkg: ctx.pkg,
    le: makeLineEnv(),
    ev: new NoteEval(),
    limits: { above: 0, below: 0 },
  };
  const time = rt.time[i] ?? Number.NaN;
  if (!Number.isFinite(time)) return null;
  // Never drawn at any frame: note alpha 0 gives an alpha byte of 0 whatever the controls do (0 * x, or NaN, clamps
  // to 0), and attachUI makes the line a UI mover for the whole chart.
  if (!((rt.alpha[i] ?? 0) > 0) || rt.ir.attachUI !== null) return null;
  const from = rt.visibleFrom[i] ?? -Infinity;
  const tStart = Math.max(0, Number.isFinite(from) ? from : 0);
  // Frames the caller needs measured exactly (CR-W2-12b-3); beyond it any longer run gives the same verdict.
  const bound =
    atLeast !== undefined && Number.isFinite(atLeast) && atLeast >= 0
      ? Math.min(APPEAR_LOOKBACK_FRAMES, Math.ceil(atLeast * 60 - 1e-9) + 1)
      : APPEAR_LOOKBACK_FRAMES;
  let k = 1;
  let earliest = Number.NaN;
  // Phase 1: the first visible frame going backwards (inside the look-back window).
  for (; k <= APPEAR_LOOKBACK_FRAMES; k++) {
    const tk = time - k * FRAME_DT;
    if (tk < tStart) break;
    if (visibleAt(probe, tk)) {
      earliest = tk;
      k++;
      break;
    }
  }
  if (Number.isNaN(earliest)) return null;
  // First visible frame already past the bound: the read time is at least that long.
  if (k - 1 >= bound) return time - earliest;
  // Phase 2: extend the run backwards while visible (clipped to the same window, and to the bound).
  for (; k <= bound; k++) {
    const tk = time - k * FRAME_DT;
    if (tk < tStart || !visibleAt(probe, tk)) break;
    earliest = tk;
  }
  return time - earliest;
}
